import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Save, Plus, Trash2, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import {
  getRule,
  createRule,
  updateRule,
  type RuleCondition,
  type RuleAction,
} from "../../services/ruleService";

const RuleFormPage = () => {
  const { ruleId } = useParams<{ ruleId: string }>();
  const navigate = useNavigate();
  const isEditMode = ruleId !== "new";

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  
  // Rule metadata
  const [ruleName, setRuleName] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState("");
  
  // Rule definition
  const [conditions, setConditions] = useState<RuleCondition[]>([
    { field: "", operator: "", value: "", conditions: "AND" }
  ]);
  const [actions, setActions] = useState<RuleAction[]>([
    { actionType: "String Manipulation" }
  ]);

  // UI state
  const [showConditions, setShowConditions] = useState(true);
  const [showActions, setShowActions] = useState(true);

  // Operators list
  const operators = [
    { value: "Equals", label: "Equals" },
    { value: "Not Equals", label: "Not Equals" },
    { value: "Contains", label: "Contains" },
    { value: "Greater Than", label: "Greater Than" },
    { value: "Less Than", label: "Less Than" },
    { value: "Greater Than or Equal", label: "Greater Than or Equal" },
    { value: "Less Than or Equal", label: "Less Than or Equal" },
  ];

  // Action types
  const actionTypes = [
    { value: "String Manipulation", label: "String Manipulation" },
    { value: "Number Manipulation", label: "Number Manipulation" },
    { value: "Date Manipulation", label: "Date Manipulation" },
  ];

  // Operations by action type
  const stringOperations = [
    { value: "replace", label: "Replace" },
    { value: "concatenate", label: "Concatenate" },
    { value: "uppercase", label: "Uppercase" },
    { value: "lowercase", label: "Lowercase" },
  ];

  const numberOperations = [
    { value: "add", label: "Add" },
    { value: "subtract", label: "Subtract" },
    { value: "multiply", label: "Multiply" },
    { value: "divide", label: "Divide" },
  ];

  const dateOperations = [
    { value: "addDays", label: "Add Days" },
    { value: "subtractDays", label: "Subtract Days" },
    { value: "format", label: "Format" },
  ];

  const getOperationsForActionType = (actionType: string) => {
    switch (actionType) {
      case "String Manipulation":
        return stringOperations;
      case "Number Manipulation":
        return numberOperations;
      case "Date Manipulation":
        return dateOperations;
      default:
        return [];
    }
  };

  useEffect(() => {
    if (isEditMode && ruleId) {
      loadRule();
    }
  }, [ruleId, isEditMode]);

  const loadRule = async () => {
    if (!ruleId) return;
    
    setLoading(true);
    try {
      const rule = await getRule(Number(ruleId));
      setRuleName(rule.ruleName);
      setDescription(rule.description || "");
      setDomain(rule.domain || "");

      // Load conditions and actions if they exist
      if (rule.conditions && rule.conditions.length > 0) {
        setConditions(rule.conditions);
      }
      if (rule.actions && rule.actions.length > 0) {
        setActions(rule.actions);
      }
    } catch (error) {
      console.error("Error loading rule:", error);
      toast.error("Failed to load rule");
      navigate("/studio/rules");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!ruleName.trim()) {
      toast.error("Rule name is required");
      return;
    }

    setSaving(true);
    try {
      if (isEditMode && ruleId) {
        await updateRule(Number(ruleId), {
          ruleName: ruleName.trim(),
          description: description.trim() || undefined,
          domain: domain.trim() || undefined,
          conditions,
          actions,
        });
      } else {
        await createRule({
          ruleName: ruleName.trim(),
          description: description.trim() || undefined,
          domain: domain.trim() || undefined,
          conditions,
          actions,
        });
      }

      toast.success(isEditMode ? "Rule updated successfully" : "Rule created successfully");
      navigate("/studio/rules");
    } catch (error) {
      console.error("Error saving rule:", error);
      toast.error("Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const addCondition = () => {
    setConditions([...conditions, { field: "", operator: "", value: "", conditions: "AND" }]);
  };

  const removeCondition = (index: number) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index));
    }
  };

  const updateCondition = (index: number, field: keyof RuleCondition, value: string) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setConditions(newConditions);
  };

  const addAction = () => {
    setActions([...actions, { actionType: "String Manipulation" }]);
  };

  const removeAction = (index: number) => {
    if (actions.length > 1) {
      setActions(actions.filter((_, i) => i !== index));
    }
  };

  const updateAction = (index: number, field: keyof RuleAction, value: string) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setActions(newActions);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading rule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/studio/rules")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Rules"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isEditMode ? "Edit Rule" : "Create New Rule"}
              </h1>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={18} />
            {saving ? "Saving..." : "Save Rule"}
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rule Name *
                </label>
                <input
                  type="text"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="Enter rule name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="Enter domain"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter rule description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>

          {/* Conditions Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <button
              onClick={() => setShowConditions(!showConditions)}
              className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <h2 className="text-lg font-semibold">Conditions</h2>
              {showConditions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {showConditions && (
              <div className="p-6">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={addCondition}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Plus size={16} />
                    Add Condition
                  </button>
                </div>
                <div className="space-y-4">
                  {conditions.map((condition, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Field
                          </label>
                          <input
                            type="text"
                            value={condition.field}
                            onChange={(e) => updateCondition(index, "field", e.target.value)}
                            placeholder="Field name"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Operator
                          </label>
                          <select
                            value={condition.operator}
                            onChange={(e) => updateCondition(index, "operator", e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">Select</option>
                            {operators.map((op) => (
                              <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Value
                          </label>
                          <input
                            type="text"
                            value={condition.value}
                            onChange={(e) => updateCondition(index, "value", e.target.value)}
                            placeholder="Value"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Logic
                          </label>
                          <select
                            value={condition.conditions || "AND"}
                            onChange={(e) => updateCondition(index, "conditions", e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="AND">AND</option>
                            <option value="OR">OR</option>
                          </select>
                        </div>
                      </div>
                      {conditions.length > 1 && (
                        <div className="flex justify-end mt-2">
                          <button
                            onClick={() => removeCondition(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove condition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <button
              onClick={() => setShowActions(!showActions)}
              className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <h2 className="text-lg font-semibold">Actions</h2>
              {showActions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {showActions && (
              <div className="p-6">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={addAction}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Plus size={16} />
                    Add Action
                  </button>
                </div>
                <div className="space-y-4">
                  {actions.map((action, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Action Type
                          </label>
                          <select
                            value={action.actionType}
                            onChange={(e) => updateAction(index, "actionType", e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          >
                            {actionTypes.map((type) => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Operation
                          </label>
                          <select
                            value={action.operation || ""}
                            onChange={(e) => updateAction(index, "operation", e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">Select</option>
                            {getOperationsForActionType(action.actionType).map((op) => (
                              <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Field
                          </label>
                          <input
                            type="text"
                            value={action.field || ""}
                            onChange={(e) => updateAction(index, "field", e.target.value)}
                            placeholder="Field name"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Value
                          </label>
                          <input
                            type="text"
                            value={action.value || ""}
                            onChange={(e) => updateAction(index, "value", e.target.value)}
                            placeholder="Value (optional)"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      {actions.length > 1 && (
                        <div className="flex justify-end mt-2">
                          <button
                            onClick={() => removeAction(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove action"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleFormPage;
